import React from 'react';
import { Link } from 'react-router-dom';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  AcademicCapIcon,
  GlobeAltIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Courses', href: '/courses' },
    { name: 'Services', href: '/services' },
    { name: 'About', href: '/about' },
    { name: 'Booking', href: '/booking' },
    { name: 'Support', href: '/support' },
    { name: 'Contact', href: '/contact' },
  ];

  const courses = [
    { name: 'Python Programming', href: '/courses/python-programming' },
    { name: 'React Development', href: '/courses/react-development' },
    { name: 'DevOps & Cloud', href: '/courses/devops-cloud' },
    { name: 'Machine Learning', href: '/courses/machine-learning' },
    { name: 'Data Analysis', href: '/courses/data-analysis' },
    { name: 'Web Development', href: '/courses/web-development' },
  ];

  const services = [
    { name: 'Project Support', href: '/services#project-support' },
    { name: 'Technical Writing', href: '/services#technical-writing' },
    { name: 'Code Debugging', href: '/services#debugging' },
    { name: 'Interview Prep', href: '/services#interview-prep' },
  ];

  const socialLinks = [
    { name: 'LinkedIn', href: 'https://linkedin.com/in/duke-nyamongo-74a043178/', icon: 'linkedin' },
    { name: 'GitHub', href: 'https://github.com/dukelester', icon: 'github' },
    { name: 'Twitter', href: 'https://twitter.com/teachwithduke', icon: 'twitter' },
    { name: 'YouTube', href: 'https://youtube.com/@teachwithduke', icon: 'youtube' },
  ];

  return (
    <footer className="bg-slate-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              <div className="h-8 w-8 bg-gradient-to-r from-indigo-600 to-cyan-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">D</span>
              </div>
              <span className="ml-2 text-xl font-bold">TeachWithDuke</span>
            </div>
            <p className="text-gray-400 mb-4">
              Empowering learners worldwide with personalized programming, data analysis, 
              DevOps, and machine learning education through 1-on-1 mentorship.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-cyan-300 transition-colors duration-200"
                  aria-label={social.name}
                >
                  <span className="sr-only">{social.name}</span>
                  <div className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center hover:bg-gray-600 transition-colors duration-200">
                    <span className="text-xs font-bold">{social.name[0]}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-cyan-300 transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Courses */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Courses</h3>
            <ul className="space-y-2">
              {courses.map((course) => (
                <li key={course.name}>
                  <Link
                    to={course.href}
                    className="text-gray-400 hover:text-cyan-300 transition-colors duration-200"
                  >
                    {course.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="text-gray-400 hover:text-cyan-300 transition-colors duration-200"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                <a
                  href="mailto:teachwithduke@gmail.com"
                  className="text-gray-400 hover:text-cyan-300 transition-colors duration-200"
                >
                  teachwithduke@gmail.com
                </a>
              </div>
              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                <a
                  href="tel:+1234567890"
                  className="text-gray-400 hover:text-cyan-300 transition-colors duration-200"
                >
                  +254745711132
                </a>
              </div>
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
                <span className="text-gray-400">Global - Online Sessions</span>
              </div>
              <div className="flex items-center">
                <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
                <span className="text-gray-400">Available Worldwide</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <AcademicCapIcon className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-gray-400">
                © {currentYear} TeachWithDuke. All rights reserved.
              </span>
            </div>
            <div className="flex items-center text-gray-400">
              <HeartIcon className="h-4 w-4 text-red-500 mr-1" />
              <span>Made with passion for teaching</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
